import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, Check, Loader2 } from "lucide-react";

/**
 * Live-search dropdown with debounced filtering.
 * Two modes:
 *   1. Local (default): pass `options` array — filters client-side.
 *   2. Async server: pass `loadOptions(query) => Promise<options>` — filters server-side (debounced 300ms).
 *
 * Props:
 *   options: [{ id, name, ...meta }]   (local mode)
 *   loadOptions: async (q) => options  (async mode)
 *   value: selected id
 *   selectedLabel: name to show when value is set but option not in current results (async mode)
 *   onChange: (id, option) => void
 *   placeholder, required, disabled, className
 *   renderMeta: optional (option) => ReactNode for sub-label
 *   testid, maxResults, autoFocus
 */
export default function Typeahead({
  options,
  loadOptions,
  value = "",
  selectedLabel = "",
  onChange,
  placeholder = "Search…",
  required = false,
  disabled = false,
  className = "",
  renderMeta,
  testid = "typeahead",
  maxResults = 50,
  autoFocus = false,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [serverList, setServerList] = useState([]);
  const [searching, setSearching] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debTimer = useRef(null);

  const isAsync = typeof loadOptions === "function";

  // Local filter (sync mode)
  const localFiltered = useMemo(() => {
    if (isAsync) return [];
    const arr = options || [];
    const q = query.trim().toLowerCase();
    if (!q) return arr.slice(0, maxResults);
    return arr
      .filter((o) => {
        const blob = `${o.name || ""} ${o.phone || ""} ${o.city || ""} ${o.type || ""}`.toLowerCase();
        return blob.includes(q);
      })
      .slice(0, maxResults);
  }, [query, options, maxResults, isAsync]);

  // Async server-side search
  useEffect(() => {
    if (!isAsync || !open) return;
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await loadOptions(query.trim());
        setServerList(res || []);
      } catch {
        setServerList([]);
      } finally { setSearching(false); }
    }, 250);
    return () => debTimer.current && clearTimeout(debTimer.current);
    // eslint-disable-next-line
  }, [query, open, isAsync]);

  // Find selected option for sync mode display
  const selectedSync = useMemo(
    () => (isAsync ? null : (options || []).find((o) => o.id === value)),
    [options, value, isAsync]
  );

  const displayLabel = isAsync ? selectedLabel : (selectedSync?.name || "");
  const filtered = isAsync ? serverList : localFiltered;

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => { setActiveIdx(0); }, [query, serverList]);

  const choose = (opt) => {
    onChange(opt.id, opt);
    setQuery("");
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIdx]) choose(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayValue = open ? query : displayLabel;

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testid}>
      <div className="relative">
        {searching ? (
          <Loader2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#C1121F] animate-spin pointer-events-none"/>
        ) : (
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
        )}
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          required={required && !value}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          data-testid={`${testid}-input`}
          className="input-base pl-7 pr-2"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
          {isAsync && searching && filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#94A3B8] flex items-center gap-2">
              <Loader2 size={11} className="animate-spin"/> Searching…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#94A3B8]">
              {isAsync ? (query ? `No matches for "${query}". Type at least 2 letters.` : "Type to search…") : `No matches${query ? ` for "${query}"` : ""}`}
            </div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.id}
                type="button"
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => choose(opt)}
                data-testid={`${testid}-option-${idx}`}
                className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 text-sm hover:bg-[#FEF2F2] ${idx === activeIdx ? "bg-[#FEF2F2]" : ""} ${opt.id === value ? "font-bold" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate">{opt.name}</div>
                  {renderMeta && (
                    <div className="text-[10px] text-[#94A3B8] truncate">{renderMeta(opt)}</div>
                  )}
                </div>
                {opt.id === value && <Check size={12} className="text-[#C1121F] flex-shrink-0"/>}
              </button>
            ))
          )}
          {!isAsync && filtered.length === maxResults && (
            <div className="px-3 py-1 text-[10px] text-[#94A3B8] border-t border-[#E5E7EB]">Showing first {maxResults} — type to refine.</div>
          )}
        </div>
      )}
    </div>
  );
}
